/**
 * Tactiv Bridge Types
 * Shared interfaces for cross-platform data exchange between GunfighterOS and OPMODE.
 *
 * Architecture: Each app writes a denormalized snapshot (~1KB) to Firestore
 * at bridges/{userId}/data/{appName}, and the other app reads + caches it in Dexie.
 */

// ============================================================================
// OPMODE -> GunfighterOS Snapshot
// ============================================================================

export interface OpmodeBridgeSnapshot {
  schemaVersion: 1;
  userId: string;
  snapshotAt: string; // ISO

  readiness: {
    score: number;         // 0-100
    level: 'red' | 'amber' | 'green';
    sleepHours: number;
    soreness: number;      // 1-10
    energy: number;        // 1-10
  } | null;

  recoveryDebt: {
    overallDebt: number;   // 0-100
    debtLevel: 'low' | 'moderate' | 'high' | 'severe';
    acwr: number;
    needsDeload: boolean;
  } | null;

  operatorScore: {
    score: number;         // 0-100
    level: string;         // elite/advanced/proficient/developing/beginner
    breakdown: {
      strength: number;
      endurance: number;
      bodyweight: number;
      recovery: number;
      consistency: number;
    };
  } | null;

  streakDays: number;
  workoutsThisWeek: number;
  bodyweight: number | null;
  opmodeArchetype: string | null;
}

// ============================================================================
// GunfighterOS -> OPMODE Snapshot
// ============================================================================

export interface GunfighterBridgeSnapshot {
  schemaVersion: 1;
  userId: string;
  snapshotAt: string; // ISO

  shooterGrade: {
    overall: number;       // 0-100
    label: string;         // 'Novice' | 'Beginner' | 'Proficient' | 'Expert' | 'Master'
  };

  platforms: {
    pistol: { score: number; sessions: number };
    rifle: { score: number; sessions: number };
    shotgun: { score: number; sessions: number };
  };

  lastSession: {
    date: string;
    protocolName: string;
    rounds: number;
    accuracy: number;
  } | null;

  totalSessions: number;
  totalRounds: number;
  averageAccuracy: number;
  criticalWeaknesses: number;
  shooterArchetype: string;
}

// ============================================================================
// Bridge Records (for Dexie caching)
// ============================================================================

export interface OpmodeBridgeRecord {
  userId: string;
  snapshot: OpmodeBridgeSnapshot;
  snapshotAt: string;
  cachedAt: string;
}

export interface GunfighterBridgeRecord {
  userId: string;
  snapshot: GunfighterBridgeSnapshot;
  snapshotAt: string;
  cachedAt: string;
}

// ============================================================================
// Readiness Helpers
// ============================================================================

export type ReadinessLevel = 'red' | 'amber' | 'green' | null;

export function getReadinessLevel(snapshot: OpmodeBridgeSnapshot | null): ReadinessLevel {
  if (!snapshot?.readiness) return null;
  return snapshot.readiness.level;
}

export function getReadinessScore(snapshot: OpmodeBridgeSnapshot | null): number | null {
  if (!snapshot?.readiness) return null;
  return snapshot.readiness.score;
}

export function getRecoveryDebtLevel(snapshot: OpmodeBridgeSnapshot | null): string | null {
  if (!snapshot?.recoveryDebt) return null;
  return snapshot.recoveryDebt.debtLevel;
}

export function isBridgeStale(snapshotAt: string): boolean {
  const snapshotTime = new Date(snapshotAt).getTime();
  const now = Date.now();
  const twentyFourHours = 24 * 60 * 60 * 1000;
  return now - snapshotTime > twentyFourHours;
}

export function getReadinessRecommendation(snapshot: OpmodeBridgeSnapshot | null): {
  text: string;
  level: 'green' | 'amber' | 'red' | 'none';
} {
  if (!snapshot?.readiness) {
    return { text: 'Readiness data unavailable', level: 'none' };
  }

  const { score } = snapshot.readiness;
  const debtLevel = snapshot.recoveryDebt?.debtLevel;

  if (debtLevel === 'severe') {
    return { text: 'Dry fire only recommended — recovery debt is severe', level: 'red' };
  }
  if (score < 40) {
    return { text: 'Light maintenance session recommended', level: 'red' };
  }
  if (score < 60 || debtLevel === 'high') {
    return { text: 'Reduced session recommended — cap rounds at 100', level: 'amber' };
  }
  return { text: 'Ready for full session', level: 'green' };
}

// ============================================================================
// Shooter Grade Helpers
// ============================================================================

export function getShooterGradeLabel(score: number): string {
  if (score >= 90) return 'Master';
  if (score >= 75) return 'Expert';
  if (score >= 60) return 'Proficient';
  if (score >= 40) return 'Beginner';
  return 'Novice';
}

export function getShooterGradeColor(label: string): { text: string; bg: string } {
  switch (label.toLowerCase()) {
    case 'master':
      return { text: 'text-operator-gold', bg: 'bg-operator-gold/20' };
    case 'expert':
      return { text: 'text-purple-400', bg: 'bg-purple-500/20' };
    case 'proficient':
      return { text: 'text-blue-400', bg: 'bg-blue-500/20' };
    case 'beginner':
      return { text: 'text-green-400', bg: 'bg-green-500/20' };
    default:
      return { text: 'text-gray-400', bg: 'bg-gray-500/20' };
  }
}

export function getDaysSinceSession(dateString: string | null | undefined): number {
  if (!dateString) return -1;
  const sessionDate = new Date(dateString);
  const now = new Date();
  return Math.floor((now.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24));
}
